import React, { useEffect } from "react";
import { Box, Button, Container, Heading, Text } from "@chakra-ui/react";
import { FaArrowRight } from "react-icons/fa";
import { motion, useReducedMotion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

// Same palette and type scale as About / Homepage.
const ACCENT = "#7AB0FF";
const BG_DARK = "#0b0f14";
const TEXT_LIGHT = "#f7f2ec";
const TEXT_LIGHT_MUTED = "rgba(247,242,236,0.78)";

// Module scope on purpose: a motion component created inside the page remounts on every render (flicker).
const MotionBox = motion(Box);

const Eyebrow: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = ACCENT }) => (
    <Text fontSize="sm" fontWeight="700" letterSpacing="0.18em" textTransform="uppercase" color={color} mb={4}>
        {children}
    </Text>
);

// Fades in once as it enters the viewport; never replays, so scrolling back and forth can't flicker.
const Reveal: React.FC<React.ComponentProps<typeof MotionBox> & { delay?: number }> = ({ delay = 0, children, ...rest }) => {
    const reduced = useReducedMotion();
    return (
        <MotionBox
            initial={reduced ? false : { opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay, ease: "easeOut" }}
            {...rest}
        >
            {children}
        </MotionBox>
    );
};

/**
 * For healthcare providers — cut right down to Sam Manger's website feedback (27 Jul 2026):
 * "The only thing we can say is healthcare providers and insurers can purchase bulk packages to
 * provide to their patients." Benefits for organisations, key features, clinical benefits and the
 * outcome claims were removed (can't be guaranteed; the rest is already on the homepage).
 */
const Providers = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box bg={BG_DARK} minH="100vh" color={TEXT_LIGHT}>
            <NavbarWithCallToAction />

            <Box as="section" bg={BG_DARK} position="relative" overflow="hidden">
                <Box
                    aria-hidden
                    position="absolute"
                    top="-20%"
                    right={{ base: "-40%", md: "-10%" }}
                    w={{ base: "120vw", md: "70vw" }}
                    h={{ base: "120vw", md: "70vw" }}
                    bg="radial-gradient(circle, rgba(122,176,255,0.16) 0%, rgba(122,176,255,0.05) 35%, transparent 65%)"
                    pointerEvents="none"
                />
                <Container maxW="1360px" minH={{ base: "70vh", md: "80vh" }} display="flex" alignItems="center" py={{ base: 16, md: 20 }} position="relative">
                    <Reveal width="100%">
                        <Eyebrow color="rgba(247,242,236,0.7)">For healthcare providers</Eyebrow>
                        <Heading
                            as="h1"
                            fontSize={{ base: "2.6rem", md: "4rem", lg: "5.2rem" }}
                            lineHeight={0.98}
                            letterSpacing="-0.04em"
                            maxW="1000px"
                            fontWeight={400}
                            mb={7}
                        >
                            SurgReady for your patients
                        </Heading>
                        <Text fontSize={{ base: "lg", md: "2xl" }} color={TEXT_LIGHT_MUTED} maxW="760px" lineHeight="1.7" mb={10}>
                            Healthcare providers and insurers can purchase SurgReady in bulk packages to provide to
                            their patients before surgery.
                        </Text>
                        <Button as={RouterLink} to="/contact" size="lg" minH="56px" bg={ACCENT} color={BG_DARK} _hover={{ bg: "#5F98FF" }} rightIcon={<FaArrowRight />} fontWeight={700}>
                            Contact us about bulk packages
                        </Button>
                    </Reveal>
                </Container>
            </Box>

            <FooterWithFourColumns />
        </Box>
    );
};

export default Providers;
